import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { X, Camera, Mic, Square, CheckCircle2, Send, Loader2, RotateCcw } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MiniConsultModal = ({ isOpen, onClose }) => {
  // Steps: welcome, conversation, problem-summary, photo-capture, review, success
  const [step, setStep] = useState('welcome');
  const [sessionId] = useState(() => `consult_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [problemSummary, setProblemSummary] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Photo capture state
  const [photos, setPhotos] = useState([]);
  const [photoLabels] = useState([
    { key: 'upper_occlusal', label: 'Upper Occlusal', instruction: 'Tilt your head down and open wide, showing top teeth from above' },
    { key: 'lower_occlusal', label: 'Lower Occlusal', instruction: 'Tilt your head back and open wide, showing bottom teeth from below' },
    { key: 'frontal_smile', label: 'Frontal Big Smile', instruction: 'Give me your biggest smile, showing all your teeth' },
    { key: 'right_smile', label: 'Right Big Smile', instruction: 'Turn your head slightly right and smile big' }
  ]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [cameraStream, setCameraStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  
  // Form state
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  
  // Initialize Speech Recognition and Synthesis
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Speech Recognition (Speech-to-Text)
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        
        recognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0])
            .map(result => result.transcript)
            .join('');
          
          setCurrentTranscript(transcript);
          
          if (event.results[0].isFinal) {
            handlePatientResponse(transcript);
          }
        };
        
        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
        
        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }
      
      // Speech Synthesis (Text-to-Speech)
      if (window.speechSynthesis) {
        synthRef.current = window.speechSynthesis;
      }
    }
    
    return () => {
      stopCamera();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);
  
  // Start conversation when entering conversation step
  useEffect(() => {
    if (step === 'conversation' && conversationHistory.length === 0) {
      const initialQuestion = "What's your number one, absolute number one concern?";
      setConversationHistory([{ role: 'assistant', content: initialQuestion }]);
      speakText(initialQuestion);
    }
  }, [step]);
  
  const speakText = async (text) => {
    if (!text) return;
    
    setIsSpeaking(true);
    
    try {
      // Call backend TTS endpoint
      const response = await fetch(`${BACKEND_URL}/api/mini-consult/text-to-speech`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          voice: 'nova',  // Energetic, friendly female voice
          speed: 1.0
        })
      });
      
      const data = await response.json();
      
      // Create audio element and play
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => {
        console.error('Audio playback error');
        setIsSpeaking(false);
      };
      await audio.play();
      
    } catch (error) {
      console.error('Error with TTS:', error);
      setIsSpeaking(false);
      // Fallback to browser speech if TTS fails
      if (synthRef.current) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.onend = () => setIsSpeaking(false);
        synthRef.current.speak(utterance);
      }
    }
  };
  
  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setCurrentTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };
  
  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };
  
  const handlePatientResponse = async (transcript) => {
    if (!transcript.trim()) return;
    
    // Add patient message to history
    const newHistory = [...conversationHistory, { role: 'user', content: transcript }];
    setConversationHistory(newHistory);
    setCurrentTranscript('');
    setIsProcessing(true);
    
    try {
      // Send to backend for AI processing
      const response = await fetch(`${BACKEND_URL}/api/mini-consult/process-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          patient_message: transcript,
          conversation_history: newHistory
        })
      });
      
      const data = await response.json();
      
      // Add AI response
      const updatedHistory = [...newHistory, { role: 'assistant', content: data.ai_response }];
      setConversationHistory(updatedHistory);
      speakText(data.ai_response);
      
      // Check if conversation is complete
      if (data.is_complete && data.problem_summary) {
        setProblemSummary(data.problem_summary);
        setTimeout(() => setStep('problem-summary'), 3000);
      }
    } catch (error) {
      console.error('Error processing conversation:', error);
      const errorMsg = "I'm sorry, I had trouble processing that. Could you try again?";
      setConversationHistory([...newHistory, { role: 'assistant', content: errorMsg }]);
      speakText(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 1280, height: 720 },
        audio: false
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraStream(stream);
        setShowCamera(true);
      }
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Unable to access camera. Please ensure camera permissions are granted.');
    }
  };
  
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setShowCamera(false);
    }
  };
  
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      const newPhotos = [...photos, photoDataUrl];
      setPhotos(newPhotos);
      
      // Move to next photo or finish
      if (currentPhotoIndex < photoLabels.length - 1) {
        setCurrentPhotoIndex(currentPhotoIndex + 1);
      } else {
        stopCamera();
        setStep('review');
      }
    }
  };
  
  const retakePhoto = () => {
    const newPhotos = [...photos];
    newPhotos.pop();
    setPhotos(newPhotos);
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };
  
  const submitConsultation = async () => {
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/mini-consult/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          patient_name: patientName,
          patient_email: patientEmail,
          patient_phone: patientPhone,
          conversation_history: conversationHistory,
          problem_summary: problemSummary,
          photos: photos,
          photo_labels: photoLabels.map(p => p.key)
        })
      });
      
      if (response.ok) {
        setStep('success');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      alert('Failed to submit consultation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#01A9B4] to-[#006A71] p-6 text-white flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Mini Consult NOW</h2>
            <p className="text-sm opacity-90">Free preliminary assessment with Dr. Kartik Patel</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Welcome Step */}
          {step === 'welcome' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-[#01A9B4]/10 rounded-full flex items-center justify-center mx-auto">
                <Camera className="w-10 h-10 text-[#01A9B4]" />
              </div>
              <h3 className="text-2xl font-bold text-[#006A71]">Welcome to Your Mini Consult!</h3>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                I'll ask you a few questions about your dental concerns using voice interaction, 
                then guide you through taking 4 clinical photos. Dr. Patel will review everything 
                and get back to you with a preliminary impression.
              </p>
              <div className="bg-[#01A9B4]/10 p-4 rounded-xl max-w-xl mx-auto">
                <p className="text-sm text-gray-700">
                  <strong>What you'll need:</strong>
                  <br />✓ Microphone access for voice conversation
                  <br />✓ Camera access for clinical photos
                  <br />✓ 5-10 minutes of your time
                </p>
              </div>
              <Button
                onClick={() => setStep('conversation')}
                size="lg"
                className="bg-[#01A9B4] hover:bg-[#006A71] text-white"
              >
                Let's Get Started
              </Button>
            </div>
          )}
          
          {/* Conversation Step */}
          {step === 'conversation' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-[#006A71] mb-2">Tell Me About Your Concern</h3>
                <p className="text-sm text-gray-600">Click the microphone to speak, or type your response</p>
              </div>
              
              {/* Conversation History */}
              <div className="space-y-4 max-h-[400px] overflow-y-auto bg-gray-50 p-4 rounded-xl">
                {conversationHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-[#01A9B4] text-white'
                          : 'bg-white border-2 border-[#01A9B4]/20 text-gray-800'
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
                
                {isProcessing && (
                  <div className="flex justify-start">
                    <div className="bg-white border-2 border-[#01A9B4]/20 p-4 rounded-2xl">
                      <Loader2 className="w-5 h-5 animate-spin text-[#01A9B4]" />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Voice Input */}
              <div className="flex items-center gap-4">
                <button
                  onClick={isListening ? stopListening : startListening}
                  disabled={isProcessing || isSpeaking}
                  className={`flex-1 p-6 rounded-xl border-2 transition-all ${
                    isListening
                      ? 'bg-red-500 text-white border-red-500 animate-pulse'
                      : 'bg-white border-[#01A9B4] text-[#01A9B4] hover:bg-[#01A9B4]/10'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center justify-center gap-3">
                    {isListening ? (
                      <>
                        <Square className="w-6 h-6" />
                        <span className="font-semibold">Stop Recording</span>
                      </>
                    ) : (
                      <>
                        <Mic className="w-6 h-6" />
                        <span className="font-semibold">
                          {isSpeaking ? 'AI is speaking...' : 'Click to Speak'}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              </div>
              
              {currentTranscript && (
                <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                  <p className="text-sm text-gray-700">
                    <strong>You're saying:</strong> {currentTranscript}
                  </p>
                </div>
              )}
              
              {/* WhatsApp Alternative */}
              <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-xl border-2 border-green-200">
                <div className="flex items-start gap-3">
                  <WhatsAppIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800 mb-1">Can't chat now?</p>
                    <p className="text-xs text-gray-600 mb-3">Message us on WhatsApp instead. Tell us about your concerns, and we'll get back to you.</p>
                    <a
                      href="https://wa.me/447787814064?text=Hi%2C%20I%27d%20like%20to%20discuss%20my%20dental%20concerns%20with%20Dr.%20Patel"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <WhatsAppIcon className="w-4 h-4" />
                      Message on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Problem Summary Step */}
          {step === 'problem-summary' && (
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#006A71] mb-2">Thank You for Sharing!</h3>
                <p className="text-gray-600">Here's what I understood about your concern:</p>
              </div>
              
              <div className="bg-[#01A9B4]/10 p-6 rounded-xl border-2 border-[#01A9B4]/20">
                <h4 className="font-bold text-[#006A71] mb-3">Your Dental Concerns:</h4>
                <div className="whitespace-pre-wrap text-gray-700">{problemSummary}</div>
              </div>
              
              <p className="text-center text-gray-600">
                Now, let's take 4 clinical photos so Dr. Patel can give you a preliminary impression.
              </p>
              
              <Button
                onClick={() => {
                  setStep('photo-capture');
                  startCamera();
                }}
                size="lg"
                className="w-full bg-[#01A9B4] hover:bg-[#006A71] text-white"
              >
                Continue to Photo Capture
              </Button>
            </div>
          )}
          
          {/* Photo Capture Step */}
          {step === 'photo-capture' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-[#006A71] mb-2">
                  Photo {currentPhotoIndex + 1} of {photoLabels.length}
                </h3>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                  {photoLabels[currentPhotoIndex].label}
                </h4>
                <p className="text-gray-600">{photoLabels[currentPhotoIndex].instruction}</p>
              </div>
              
              {showCamera && (
                <div className="relative bg-black rounded-xl overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                    <Button
                      onClick={capturePhoto}
                      size="lg"
                      className="bg-[#01A9B4] hover:bg-[#006A71] text-white"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Capture Photo
                    </Button>
                    {photos.length > 0 && (
                      <Button
                        onClick={retakePhoto}
                        size="lg"
                        variant="outline"
                        className="border-white text-white hover:bg-white/20"
                      >
                        <RotateCcw className="w-5 h-5 mr-2" />
                        Retake Previous
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Photos captured so far */}
              <div className="flex gap-2 flex-wrap">
                {photos.map((photo, idx) => (
                  <img
                    key={idx}
                    src={photo}
                    alt={`Captured ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-[#01A9B4]"
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Review Step */}
          {step === 'review' && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-[#006A71] text-center mb-6">
                Review & Send to Dr. Patel
              </h3>
              
              {/* Patient Info Form */}
              <div className="bg-gray-50 p-6 rounded-xl space-y-4">
                <h4 className="font-bold text-gray-800">Your Contact Information</h4>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-[#01A9B4] outline-none"
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={patientEmail}
                  onChange={(e) => setPatientEmail(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-[#01A9B4] outline-none"
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={patientPhone}
                  onChange={(e) => setPatientPhone(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-[#01A9B4] outline-none"
                  required
                />
              </div>
              
              {/* Problem Summary */}
              <div className="bg-[#01A9B4]/10 p-4 rounded-xl">
                <h4 className="font-bold text-[#006A71] mb-2">Your Concerns:</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{problemSummary}</p>
              </div>
              
              {/* Photos */}
              <div>
                <h4 className="font-bold text-gray-800 mb-3">Clinical Photos ({photos.length})</h4>
                <div className="grid grid-cols-2 gap-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={photo}
                        alt={photoLabels[idx]?.label}
                        className="w-full h-40 object-cover rounded-lg border-2 border-[#01A9B4]/20"
                      />
                      <p className="text-xs text-gray-600 mt-1">{photoLabels[idx]?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button
                onClick={submitConsultation}
                disabled={!patientName || !patientEmail || !patientPhone || isProcessing}
                size="lg"
                className="w-full bg-[#01A9B4] hover:bg-[#006A71] text-white disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send to Dr. Patel
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center space-y-6 py-8">
              <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto" />
              <h3 className="text-2xl font-bold text-[#006A71]">Consultation Submitted!</h3>
              <p className="text-lg text-gray-700 max-w-xl mx-auto">
                Thank you for completing your Mini Consult! Dr. Kartik Patel will review your 
                concerns and photos, and get back to you within 24-48 hours with a preliminary 
                impression and next steps.
              </p>
              <div className="bg-[#01A9B4]/10 p-6 rounded-xl max-w-xl mx-auto">
                <p className="text-sm text-gray-700">
                  <strong>What happens next?</strong>
                  <br />✓ Dr. Patel will review your consultation
                  <br />✓ You'll receive an email with his preliminary assessment
                  <br />✓ If treatment is needed, we'll help you book an appointment
                </p>
              </div>
              <Button
                onClick={onClose}
                size="lg"
                className="bg-[#01A9B4] hover:bg-[#006A71] text-white"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiniConsultModal;
